import type {
  UnitTransformer,
  UnitRecord,
  CSVRow,
  AppfolioUnitRow,
  UnitUploadContext,
  ValidationResult,
} from '@/types/csv-upload'
import { extractPropertyCompositeFromColumn } from './appfolio-utils'

// Unit ID and Property ID columns are absent in AppFolio exports that omit numeric IDs.
// externalId falls back to a Property::UnitName composite; propertyExternalId to the Property column.
export const APPFOLIO_UNIT_REQUIRED_FIELDS = ['Unit Name', 'Property'] as const

export const appfolioUnitTransformer: UnitTransformer = {
  providerName: 'appfolio',

  transformUnit(row: CSVRow, context: UnitUploadContext): UnitRecord | null {
    const r = row as AppfolioUnitRow

    // Skip AppFolio separator rows (Unit Name starts with "->") — structural, not a data warning
    const rawName = r['Unit Name']?.trim()
    if (rawName?.startsWith('->')) {
      context.silentSkip = true
      return null
    }

    // Name: Unit Name → Unit Street Address 1 → Unit Address
    const name =
      rawName || r['Unit Street Address 1']?.trim() || r['Unit Address']?.trim()
    if (!name) {
      context.skipReason = 'Cannot determine unit name'
      return null
    }

    // propertyExternalId: numeric Property ID when present, otherwise a synthetic name+address composite extracted from the Property column
    const rawProperty = r['Property']?.trim()
    const propertyExternalId =
      r['Property ID']?.trim() ||
      (rawProperty
        ? extractPropertyCompositeFromColumn(rawProperty)
        : undefined)
    if (!propertyExternalId) {
      context.skipReason = 'Missing Property ID or Property column'
      return null
    }

    // externalId: numeric Unit ID when present, otherwise a synthetic "<propertyComposite>::<unitName>" key
    const externalId =
      r['Unit ID']?.trim() ||
      (propertyExternalId && name
        ? `${propertyExternalId}::${name}`
        : undefined)

    // Unit address: prefer Street Address 2 (unit designator), fall back to Street Address 1
    const unitAddress =
      r['Unit Street Address 2']?.trim() ||
      r['Unit Street Address 1']?.trim() ||
      undefined

    return { externalId, name, unitAddress, propertyExternalId }
  },

  validateUnit(
    record: UnitRecord,
    _context: UnitUploadContext,
  ): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!record.name) {
      errors.push('Unit name is required — check the "Unit Name" column')
    }
    if (!record.propertyExternalId) {
      warnings.push(
        'No property identifier — check "Property ID" or "Property" column',
      )
    }

    return { isValid: errors.length === 0, errors, warnings }
  },
}
